import type * as React from 'react';

import { cn } from '@dfq/shared/lib/utils';
import { Skeleton } from '@dfq/shared/components/ui/skeleton';

export type ConfiguratorPageSkeletonProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Full-page loading skeleton for the step-by-step configurator wizard.
 *
 * Approximates the three-zone layout (progress bar strip, step content area,
 * price summary panel) without any interactive controls. Used as:
 *   1. The `<Suspense>` fallback while the wizard chunk lazy-loads.
 *   2. The in-view loading state while `configStatus === 'loading'`.
 *
 * SF-CONFIG-004.
 */
export function ConfiguratorPageSkeleton({
  className,
  ...rest
}: ConfiguratorPageSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading configurator"
      className={cn('flex min-h-screen flex-col', className)}
      {...rest}
    >
      {/* Zone 1: Progress bar strip */}
      <div className="border-border border-b px-6 py-4">
        <Skeleton className="h-3 w-full rounded-full" />
        <div className="mt-3 flex gap-4">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} className="h-4 w-12" />
          ))}
        </div>
      </div>

      {/* Zone 2 + 3: Step content + price summary */}
      <div className="flex flex-1 flex-col gap-6 px-6 py-8 lg:grid lg:grid-cols-[1fr_320px] lg:items-start lg:gap-8">
        {/* Step content area */}
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))}
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>

        {/* Price summary panel */}
        <div className="space-y-4 rounded-lg border p-4 lg:sticky lg:top-4">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-24" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>

      <span className="sr-only">Loading configurator</span>
    </div>
  );
}
